import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import type {JOB_INFORMATION} from 'src/types/crm';

import {
  BodyMedium,
  BodyMediumBold,
  BodySmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {ClickableIcon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {
  Table,
  BasicSingleCell,
} from '@spaced-out/ui-design-system/lib/components/Table';

import {
  converJobTypeString,
  getExperience,
  getRangeLabel,
  useAopEnabled,
  useWorkplaceTypeEnabled,
} from 'src/utils/crm';
import {useInflight} from 'src/hooks/redux';

import {useSelector, useDispatch} from 'react-redux';
import {
  selectJobInformation,
  selectLocations,
  selectUserInfo,
  selectHasCrmPermission,
} from 'src/selectors/crm';
import {
  getJobInformation,
  updateCrmJobInformationData,
  getLocationList,
} from 'src/action-creators/crm';
import {CRM_PERMISSIONS} from 'src/utils/crm-new/common';
import {getWorkplaceTypes, integrationTypes} from 'src/components/crm/constant';
import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {
  Toast,
  toastApi,
  ToastTitle,
  TOAST_SEMANTIC,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import * as reduxApi from 'src/utils/redux-api';
import css from './basic-job-details.css';


export const BasicJobDetailsTab = ({jobId}: {jobId?: string}) => {
  const {t} = useLabelTranslation();
  const isAopEnabled = useAopEnabled();
  const jobInfo = useSelector(selectJobInformation);
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUserInfo);
  const locations = useSelector(selectLocations);
  const viewJobSalary = selectHasCrmPermission(
    userDetails,
    CRM_PERMISSIONS.VIEW_JOB_SALARY,
  );
  React.useEffect(() => {
    if (jobId && Number(jobId) !== Number(jobInfo?.jobId)) {
      dispatch(getJobInformation(jobId));
    }
    if (!locations) {
      dispatch(getLocationList());
    }
  }, [jobId, locations]);

  const workplaceTypes = getWorkplaceTypes(t);
  const isWorkplaceTypeEnabled = useWorkplaceTypeEnabled();

  const {organization: organizationInfo, user: userInfo} =
    useSelector(selectUserInfo) ?? {};

  const {integrationType = 'NONE'} = organizationInfo ?? {};
  const isSFIntegrationEnabled =
    integrationType === integrationTypes.SUCCESSFACTORS;

  const findOfficeLocation = (officeId: any) => {
    if (!locations) {
      return '-';
    }
    const officeName =
      // @ts-expect-error - TS2339 - Property 'find' does not exist on type 'JOB_INFORMATION'. | TS7006 - Parameter 'item' implicitly has an 'any' type.
      locations.find((item) => item.id === officeId)?.name || '-';
    return officeName;
  };

  const getSalaryRange = (jobInfo: any) => {
    if (jobInfo.salaryEnd === 0) {
      return '-';
    }
    return `${jobInfo.salaryStart || 0}-${jobInfo.salaryEnd} ${
      jobInfo.salaryCurrency || ''
    }`.trim();
  };

  const getDepartments = (departments: any) => {
    if (!departments) {
      return [];
    }
    // @ts-expect-error - TS7006 - Parameter 'department' implicitly has an 'any' type.
    return departments.map((department) => ({
      label: department.levelName,
      value: department.name || '-',
      id: department.id,
    }));
  };

  const tableEntries = [
    jobInfo?.code && {label: t('Id', 'Id'), value: jobInfo.code},
    {
      label: t('Job_Title', 'Job Title'),
      value: jobInfo.title ?? '-',
      id: 'title',
    },
    isAopEnabled
      ? {
          label: t('Role', 'Role'),
          value: jobInfo.employeeRoleName ?? '-',
          id: 'employeeRole',
        }
      : null,
    {
      label: t('Employment_Type', 'Employment Type'),
      value: converJobTypeString(jobInfo.jobType),
      id: 'empType',
    },
    {
      label: t('Open_Positions', 'Open Positions'),
      value: jobInfo.openPositions,
      id: 'positionCount',
    },
    {
      label: t('Job_Experience_years', 'Job Experience (years)'),
      value: isSFIntegrationEnabled ? (
        <SFExperienceInput jobInfo={jobInfo} />
      ) : (
        getExperience(jobInfo.experienceStart, jobInfo.experienceEnd, t)
      ),
      id: 'experience',
    },
    viewJobSalary
      ? {
          label: getRangeLabel(jobInfo.employeeType.toLowerCase() || '-', t),
          value: getSalaryRange(jobInfo),
          id: 'salaryRange',
        }
      : null,
    ...getDepartments(jobInfo.departments),
    {
      label: t('Grade', 'Grade'),
      value: jobInfo?.gradeName || 'NA',
      id: 'grade',
    },
    {
      label: t('Office_Location', 'Office Location'),
      value: findOfficeLocation(jobInfo?.officeId) ?? '',
      id: 'officeLocation',
    },
    {
      label: t('Location_External', 'Location (External)'),
      value: jobInfo?.location ?? '',
      id: 'locationExternal',
    },
    isWorkplaceTypeEnabled && {
      label: t('Workplace_Type', 'Workplace Type'),
      value:
        workplaceTypes.find((type) => type.key === jobInfo?.workplaceType)
          ?.label || '-',
      id: 'workplaceType',
    },
  ].filter(Boolean);

  // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
  const BasicTableName = ({data}) => (
    <BasicSingleCell>
      <BodySmall color="secondary">{data.label}</BodySmall>
    </BasicSingleCell>
  );

  // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
  const BasicTableValue = ({data}) => (
    <BasicSingleCell>
      <BodyMediumBold>{data.value}</BodyMediumBold>
    </BasicSingleCell>
  );

  return (
    <Table
      borderRadius="12px"
      entries={tableEntries}
      headers={[
        {
          label: t('Job_Attribute', 'Job Attribute'),
          key: 'label',
          sortable: false,
          className: css.smallColumn,
          render: BasicTableName,
        },
        {
          label: t('Details', 'Details'),
          key: 'value',
          sortable: false,
          render: BasicTableValue,
        },
      ]}
    />
  );
};

const SFExperienceInput = ({jobInfo}: {jobInfo: JOB_INFORMATION}) => {
  const {t} = useLabelTranslation();
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [experienceStart, setExperienceStart] = React.useState(0);
  const [experienceEnd, setExperienceEnd] = React.useState(0);

  const handleEdit = () => {
    setExperienceStart(jobInfo.experienceStart ?? 0);
    setExperienceEnd(jobInfo.experienceEnd ?? 0);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (experienceStart > experienceEnd) {
      toastApi.show(
        <Toast semantic={TOAST_SEMANTIC.danger}>
          <ToastTitle>
            {t(
              'Experience_Start_Cannot_Be_Greater_Than_Experience_End',
              'Experience Start Cannot Be Greater Than Experience End',
            )}
          </ToastTitle>
        </Toast>,
      );
      return;
    }
    setIsSaving(true);
    try {
      const response = await dispatch(
        reduxApi.post(
          `/api/jobs/${jobInfo.id}`,
          {
            ...jobInfo,
            experienceStart,
            experienceEnd,
          },
          {},
          {apiPath: '/trmapi'},
        ),
      );
      dispatch(updateCrmJobInformationData(response as JOB_INFORMATION));
      toastApi.show(
        <Toast semantic={TOAST_SEMANTIC.success}>
          <ToastTitle>
            {t(
              'Experience_Updated_Successfully',
              'Experience Updated Successfully',
            )}
          </ToastTitle>
        </Toast>,
      );
      setIsEditing(false);
    } catch (e: any) {
      toastApi.show(
        <Toast semantic={TOAST_SEMANTIC.danger}>
          <ToastTitle>
            {e?.response?.error?.message ||
              t('Error_Updating_Experience', 'Error Updating Experience')}
          </ToastTitle>
        </Toast>,
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (isEditing) {
    return (
      <div className={css.experienceInputContainer}>
        <Input
          type="number"
          disallowExponents
          size="small"
          placeholder={t('Min_exp', 'Min exp')}
          value={String(experienceStart)}
          onChange={(e) => setExperienceStart(Number(e.target.value))}
        />
        <Input
          type="number"
          disallowExponents
          size="small"
          placeholder={t('Max_exp', 'Max exp')}
          value={String(experienceEnd)}
          onChange={(e) => setExperienceEnd(Number(e.target.value))}
        />
        <Button
          onClick={handleSave}
          type="secondary"
          isLoading={isSaving}
          iconRightName="check"
          size="small"
        />
        <Button
          onClick={handleCancel}
          type="danger"
          disabled={isSaving}
          classNames={{
            wrapper: css.trashButton,
            icon: css.trashIcon,
          }}
          iconRightName="xmark"
          size="small"
        />
      </div>
    );
  }

  return (
    <>
      {jobInfo.experienceStart ?? 0} - {jobInfo.experienceEnd ?? 0}{' '}
      {t('Years', 'Years')}
      <ClickableIcon
        className={css.editIcon}
        name="pen"
        size="small"
        color="clickable"
        onClick={handleEdit}
      />
    </>
  );
};
